// Minimal EBML (WebM/Matroska) reader — just enough to pull Opus packets out of
// a MediaRecorder capture. A linear tokenizer: containers we care about are
// descended into, every other element is skipped by its declared size, so
// alignment is preserved without modelling the whole tree. Unknown-size
// containers (MediaRecorder streams use them for Segment and Cluster) are
// descended into with the parent's end as the bound — legal here because after
// a container's children the tokenizer simply reads the next sibling's id.

export const ID_EBML = 0x1a45dfa3;
export const ID_SEGMENT = 0x18538067;
export const ID_TRACKS = 0x1654ae6b;
export const ID_TRACK_ENTRY = 0xae;
export const ID_TRACK_NUMBER = 0xd7;
export const ID_CODEC_ID = 0x86;
export const ID_CODEC_PRIVATE = 0x63a2;
export const ID_AUDIO = 0xe1;
export const ID_CHANNELS = 0x9f;
export const ID_CLUSTER = 0x1f43b675;
export const ID_SIMPLE_BLOCK = 0xa3;
export const ID_BLOCK_GROUP = 0xa0;
export const ID_BLOCK = 0xa1;

const DESCEND = new Set<number>([
  ID_SEGMENT,
  ID_TRACKS,
  ID_TRACK_ENTRY,
  ID_AUDIO,
  ID_CLUSTER,
  ID_BLOCK_GROUP,
]);

export class EbmlError extends Error {}

interface Vint {
  value: number;
  length: number;
  unknown: boolean;
}

/** Element IDs keep their marker bits — 0xAE is 0xAE, 0x1A45DFA3 is four bytes. */
function readId(bytes: Uint8Array, pos: number): Vint {
  const first = bytes[pos];
  if (first === undefined || first === 0) {
    throw new EbmlError(`bad element id at ${pos}`);
  }
  let length = 1;
  for (let mask = 0x80; (first & mask) === 0; mask >>= 1) {
    length++;
    if (length > 4) {
      throw new EbmlError(`element id too long at ${pos}`);
    }
  }
  let value = 0;
  for (let i = 0; i < length; i++) {
    const byte = bytes[pos + i];
    if (byte === undefined) {
      throw new EbmlError('truncated id');
    }
    value = value * 256 + byte;
  }
  return { value, length, unknown: false };
}

/** Sizes strip the marker bit; an all-ones value means "unknown size". */
function readSize(bytes: Uint8Array, pos: number): Vint {
  const first = bytes[pos];
  if (first === undefined || first === 0) {
    throw new EbmlError(`bad size at ${pos}`);
  }
  let length = 1;
  let mask = 0x80;
  while ((first & mask) === 0) {
    length++;
    mask >>= 1;
    if (length > 8) {
      throw new EbmlError(`size too long at ${pos}`);
    }
  }
  let value = first & (mask - 1);
  let allOnes = value === mask - 1;
  for (let i = 1; i < length; i++) {
    const byte = bytes[pos + i];
    if (byte === undefined) {
      throw new EbmlError('truncated size');
    }
    if (byte !== 0xff) {
      allOnes = false;
    }
    value = value * 256 + byte;
  }
  return { value, length, unknown: allOnes };
}

export interface EbmlToken {
  id: number;
  /** Absolute offset where the element's data starts. */
  dataStart: number;
  /** Absolute offset where the element's data ends (parent end when unknown). */
  dataEnd: number;
  /** Whether the tokenizer descends into this element's children. */
  container: boolean;
}

/** Walks the buffer, calling [visit] for every element the tokenizer sees.
 *  Containers in the descend set are entered; everything else is skipped. */
export function walkEbml(bytes: Uint8Array, visit: (token: EbmlToken) => void): void {
  walkRange(bytes, 0, bytes.length, visit);
}

function walkRange(
  bytes: Uint8Array,
  start: number,
  end: number,
  visit: (token: EbmlToken) => void,
): void {
  let pos = start;
  while (pos < end) {
    const id = readId(bytes, pos);
    const size = readSize(bytes, pos + id.length);
    const dataStart = pos + id.length + size.length;
    const dataEnd = size.unknown ? end : Math.min(dataStart + size.value, end);
    const container = DESCEND.has(id.value);
    visit({ id: id.value, dataStart, dataEnd, container });
    if (container) {
      walkRange(bytes, dataStart, dataEnd, visit);
      // An unknown-size container consumed everything to its bound.
      pos = size.unknown ? dataEnd : dataEnd;
    } else {
      if (size.unknown) {
        throw new EbmlError(`unknown-size leaf 0x${id.value.toString(16)}`);
      }
      pos = dataEnd;
    }
  }
}

export function readUint(bytes: Uint8Array, start: number, end: number): number {
  let value = 0;
  for (let i = start; i < end; i++) {
    value = value * 256 + bytes[i]!;
  }
  return value;
}

export function readString(bytes: Uint8Array, start: number, end: number): string {
  return new TextDecoder().decode(bytes.subarray(start, end));
}
